import {
  Box,
  Card,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { filter } from "lodash";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import TableListHead from "../../sections/TableListHead";
import TableListToolbar from "../../sections/TableListToolbar";
import Iconify from "../iconify/Iconify";
import Scrollbar from "../scrollbar";
import "./AppTable.css";
import { formatCurrency } from "../../utils/currency";
import TableEmptyState from "./TableEmptyState";

function AppTable({
  tableHead,
  tableRows,
  tableBody,
  searchPlaceholder,
  listType,
  toggleSwitchStatus,
  toggleSwitchAny,
  editActionClick,
  deleteActionClick,
  viewActionClick,
  downloadActionClick,
  cellClick,
  storeId,
  searchField = "name",
  hideSearch = false,
  handleAddAction,
  emptyTitle,
  emptyDesc,
  emptyButtonLabel,
  isLoading,
}) {
  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function applySortFilter(array, comparator, query) {
    const stabilizedThis = array?.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    if (query) {
      return filter(
        array,
        (_row) =>
          _row[searchField].toLowerCase().indexOf(query?.toLowerCase()) !== -1,
      );
    }
    return stabilizedThis.map((el) => el[0]);
  }

  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("action");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [selectedRow, setSelectedRow] = useState({});

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = tableRows.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const emptyRows =
    tableRows &&
    (page > 0 ? Math.max(0, (1 + page) * rowsPerPage - tableRows.length) : 0);
  const filteredRows =
    tableRows &&
    applySortFilter(tableRows, getComparator(order, orderBy), filterName);
  const isNotFound = !filteredRows.length && !!filterName;

  const dineTableBody = (row, selectedTableRow) => (
    <TableRow
      hover
      key={row?.id}
      tabIndex={-1}
      role="checkbox"
      selected={selectedTableRow}
    >
      <TableCell component="th" scope="row">
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle2" noWrap>
            {row?.name}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell align="center">
        <span
          style={{
            padding: 10,
            textDecoration: "underline",
            fontWeight: 700,
            color: "blue",
            cursor: "pointer",
          }}
          onClick={(event) => cellClick("no_of_tables", event, row)}
        >
          {row?.tables?.length}
        </span>
      </TableCell>
      <TableCell align="center">
        <Switch
          value={row?.record_status}
          checked={row?.record_status === "A" ? true : false}
          onChange={(event) => toggleSwitchStatus(event, row)}
        />
        <Typography
          component="span"
          className={
            row?.record_status === "A" ? "record-active" : "record-deactive"
          }
        >
          {row.record_status === "A" ? "Active" : "Hidden"}
        </Typography>
      </TableCell>
      <TableCell align="left">{row?.created_at}</TableCell>
      <TableCell align="right">
        <Box className="table-actions">
          <Tooltip title="Edit">
            <IconButton
              onClick={(event) => {
                editActionClick(event, row);
              }}
            >
              <Iconify icon={"eva:edit-fill"} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              sx={{ color: "error.main" }}
              onClick={(event) => {
                deleteActionClick(event, row);
              }}
            >
              <Iconify icon={"eva:trash-2-outline"} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );

  const appSubscriptionsTableBody = (row, selectedTableRow) => (
    <TableRow
      hover
      key={row?.uid}
      tabIndex={-1}
      role="checkbox"
      selected={selectedTableRow}
    >
      <TableCell component="th" scope="row">
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle2" noWrap>
            {row?.name}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell align="left">{row?.startDate}</TableCell>
      <TableCell align="left">{row?.expiryDate}</TableCell>
      <TableCell align="left">{row?.remainingDays}</TableCell>
      <TableCell align="left">₹ {row?.amount}</TableCell>
      <TableCell align="left">{row?.status}</TableCell>
      <TableCell align="left">{row?.paymentDate}</TableCell>
      <TableCell align="right">
        <Box className="table-actions" sx={{ textAlign: "center" }}>
          <Tooltip title="Download Invoice">
            <IconButton
              onClick={(event) => {
                downloadActionClick(event, row);
              }}
            >
              <Iconify icon="ic:round-download" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );

  const customerTableBody = (row, selectedTableRow) => (
    <TableRow
      hover
      key={row?.uid}
      tabIndex={-1}
      role="checkbox"
      selected={selectedTableRow}
    >
      <TableCell component="th" scope="row">
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          className="view-link-wrap"
        >
          <Typography variant="subtitle2" noWrap>
            <Link to={`/app/customers/view/${row.uid}`} className="view-link">
              {row?.first_name} {row?.last_name}
            </Link>
          </Typography>
        </Stack>
      </TableCell>
      <TableCell align="left">{row?.mobile_no}</TableCell>
      <TableCell align="left">{row?.email}</TableCell>
      <TableCell align="left">{row?.city}</TableCell>
      <TableCell align="left">{row?.created_at}</TableCell>
      <TableCell align="right">
        <Box className="table-actions">
          <Tooltip title="Edit">
            <IconButton
              onClick={(event) => {
                editActionClick(event, row);
              }}
            >
              <Iconify icon={"eva:edit-fill"} />
            </IconButton>
          </Tooltip>
          <Tooltip title="View">
            <IconButton
              onClick={(event) => {
                viewActionClick(event, row);
              }}
            >
              <Iconify icon={"carbon:view"} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );

  const userTableBody = (row, selectedTableRow) => (
    <TableRow
      hover
      key={row?.uid}
      tabIndex={-1}
      role="checkbox"
      selected={selectedTableRow}
    >
      <TableCell component="th" scope="row">
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          className="view-link-wrap"
        >
          <Typography variant="subtitle2" noWrap>
            <Link to={`/app/users/view/${row.uid}`} className="view-link">
              {row?.name}
            </Link>
          </Typography>
        </Stack>
      </TableCell>
      <TableCell align="left">{row?.mobile_no}</TableCell>
      <TableCell align="left">
        {row?.roles?.map((role) => role.role_name).join(", ")}
      </TableCell>
      <TableCell align="left">
        <Switch
          value={row?.record_status}
          checked={row?.record_status === "A" ? true : false}
          onChange={(event) => toggleSwitchStatus(event, row)}
        />
        <Typography
          component="span"
          className={
            row?.record_status === "A" ? "record-active" : "record-deactive"
          }
        >
          {row.record_status === "A" ? "Active" : "Hidden"}
        </Typography>
      </TableCell>
      <TableCell align="left">{row?.created_at}</TableCell>
      <TableCell align="right">
        <Box className="table-actions">
          <Tooltip title="Edit">
            <IconButton
              onClick={(event) => {
                editActionClick(event, row);
              }}
            >
              <Iconify icon={"eva:edit-fill"} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              sx={{ color: "error.main" }}
              onClick={(event) => {
                deleteActionClick(event, row);
              }}
            >
              <Iconify icon={"eva:trash-2-outline"} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );

  const salesSummaryTableBody = (row, selectedTableRow) => (
    <TableRow
      hover
      key={row?.date}
      tabIndex={-1}
      role="checkbox"
      selected={selectedTableRow}
    >
      <TableCell align="left">{row?.date}</TableCell>
      <TableCell align="left">{row?.orders}</TableCell>
      <TableCell align="left">{row?.grossSales}</TableCell>
      <TableCell align="left">{row?.discounts}</TableCell>
      <TableCell align="left">{row?.newSales}</TableCell>
    </TableRow>
  );

  const paymentMethodTableBody = (row, selectedTableRow) => (
    <TableRow
      hover
      key={row?.paymentMethodId}
      tabIndex={-1}
      role="checkbox"
      selected={selectedTableRow}
    >
      <TableCell align="left">{row?.paymentMethod}</TableCell>
      <TableCell align="left">{row?.orders}</TableCell>
      <TableCell align="left">{row?.sales}</TableCell>
    </TableRow>
  );

  const categorySalesTableBody = (row, selectedTableRow) => (
    <TableRow
      hover
      key={row?.categoryId}
      tabIndex={-1}
      role="checkbox"
      selected={selectedTableRow}
    >
      <TableCell align="left">{row?.categoryName}</TableCell>
      <TableCell align="left">{row?.quantity}</TableCell>
      <TableCell align="left">{row?.sales}</TableCell>
      <TableCell align="left">{row?.share}%</TableCell>
    </TableRow>
  );

  const categoriesTableBody = (row, selectedTableRow) => (
    <TableRow
      hover
      key={row?.uid}
      tabIndex={-1}
      role="checkbox"
      selected={selectedTableRow}
    >
      <TableCell component="th" scope="row">
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle2" noWrap>
            {row?.name}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell align="center">
        <Switch
          value={row?.record_status}
          checked={row?.record_status === "A" ? true : false}
          onChange={(event) => toggleSwitchStatus(event, row)}
        />
        <Typography
          component="span"
          className={
            row?.record_status === "A" ? "record-active" : "record-deactive"
          }
        >
          {row.record_status === "A" ? "Active" : "Hidden"}
        </Typography>
      </TableCell>
      <TableCell align="left">{row?.created_at}</TableCell>
      <TableCell align="right">
        <Box className="table-actions">
          <Tooltip title="Edit">
            <IconButton
              onClick={(event) => {
                editActionClick(event, row);
              }}
            >
              <Iconify icon={"eva:edit-fill"} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              sx={{ color: "error.main" }}
              onClick={(event) => {
                deleteActionClick(event, row);
              }}
            >
              <Iconify icon={"eva:trash-2-outline"} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );

  const itemsTableBody = (row, selectedTableRow) => (
    <TableRow
      hover
      key={row?.product_uid}
      tabIndex={-1}
      role="checkbox"
      selected={selectedTableRow}
    >
      <TableCell component="th" scope="row">
        <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          className="view-link-wrap"
        >
          <Typography variant="subtitle2" noWrap>
            <Link
              to={`/config/item/view/${row.product_uid}`}
              className="view-link"
            >
              {row?.product_name}
            </Link>
          </Typography>
        </Stack>
      </TableCell>
      <TableCell align="left">{row?.product_category_name}</TableCell>
      <TableCell align="center">
        {formatCurrency(row?.original_price)}
      </TableCell>
      <TableCell align="center">
        {formatCurrency(row?.discounted_price)}
      </TableCell>
      <TableCell align="center">
        <Switch
          value={row?.is_favorite}
          checked={row?.is_favorite === 1 ? true : false}
          onChange={(event) => toggleSwitchAny(event, row)}
        />
        <Typography
          component="span"
          className={
            row?.is_favorite === 1 ? "record-active" : "record-deactive"
          }
        >
          {row.is_favorite === 1 ? "Yes" : "No"}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Switch
          value={row?.is_published}
          checked={row?.is_published === 1 ? true : false}
          onChange={(event) => toggleSwitchStatus(event, row)}
        />
        <Typography
          component="span"
          className={
            row?.is_published === 1 ? "record-active" : "record-deactive"
          }
        >
          {row.is_published === 1 ? "Publish" : "Draft"}
        </Typography>
      </TableCell>
      <TableCell align="left">{row?.created_at}</TableCell>
      <TableCell align="right">
        <Box className="table-actions">
          <Tooltip title="View">
            <IconButton
              onClick={(event) => {
                viewActionClick(event, row);
              }}
            >
              <Iconify icon={"carbon:view"} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              onClick={(event) => {
                editActionClick(event, row);
              }}
            >
              <Iconify icon={"eva:edit-fill"} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              sx={{ color: "error.main" }}
              onClick={(event) => {
                deleteActionClick(event, row);
              }}
            >
              <Iconify icon={"eva:trash-2-outline"} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
  const addonsTableBody = (row, selectedTableRow) => (
    <TableRow
      hover
      key={row?.uid}
      tabIndex={-1}
      role="checkbox"
      selected={selectedTableRow}
    >
      <TableCell component="th" scope="row">
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle2" noWrap>
            {row?.name}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell align="center">{formatCurrency(row?.price)}</TableCell>
      <TableCell align="left">{row?.addon_group_name}</TableCell>
      <TableCell align="center">
        <Switch
          value={row?.record_status}
          checked={row?.record_status === "A" ? true : false}
          onChange={(event) => toggleSwitchStatus(event, row)}
        />
        <Typography
          component="span"
          className={
            row?.record_status === "A" ? "record-active" : "record-deactive"
          }
        >
          {row.record_status === "A" ? "Active" : "Hidden"}
        </Typography>
      </TableCell>
      <TableCell align="left">{row?.created_at}</TableCell>
      <TableCell align="right">
        <Box className="table-actions">
          <Tooltip title="Edit">
            <IconButton
              onClick={(event) => {
                editActionClick(event, row);
              }}
            >
              <Iconify icon={"eva:edit-fill"} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              sx={{ color: "error.main" }}
              onClick={(event) => {
                deleteActionClick(event, row);
              }}
            >
              <Iconify icon={"eva:trash-2-outline"} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
  const attributeTableBody = (row, selectedTableRow) => (
    <TableRow
      hover
      key={row?.uid}
      tabIndex={-1}
      role="checkbox"
      selected={selectedTableRow}
    >
      <TableCell component="th" scope="row">
        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle2" noWrap>
            {row?.name}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell align="center">
        <span
          style={{
            padding: 10,
            textDecoration: "underline",
            fontWeight: 700,
            color: "blue",
            cursor: "pointer",
          }}
          onClick={(event) => cellClick("no_of_attr_values", event, row)}
        >
          {row?.attributeValues?.length}
        </span>
      </TableCell>
      <TableCell align="center">
        <Switch
          value={row?.record_status}
          checked={row?.record_status === "A" ? true : false}
          onChange={(event) => toggleSwitchStatus(event, row)}
        />
        <Typography
          component="span"
          className={
            row?.record_status === "A" ? "record-active" : "record-deactive"
          }
        >
          {row.record_status === "A" ? "Active" : "Hidden"}
        </Typography>
      </TableCell>
      <TableCell align="left">{row?.created_at}</TableCell>
      <TableCell align="right">
        <Box className="table-actions">
          <Tooltip title="View">
            <IconButton
              onClick={(event) => {
                viewActionClick(event, row);
              }}
            >
              <Iconify icon={"carbon:view"} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              onClick={(event) => {
                editActionClick(event, row);
              }}
            >
              <Iconify icon={"eva:edit-fill"} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              sx={{ color: "error.main" }}
              onClick={(event) => {
                deleteActionClick(event, row);
              }}
            >
              <Iconify icon={"eva:trash-2-outline"} />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <Card>
        {!hideSearch && (
          <TableListToolbar
            numSelected={selected?.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
            searchPlaceholder={searchPlaceholder}
          />
        )}
        <Scrollbar>
          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <TableListHead
                order={order}
                orderBy={orderBy}
                headLabel={tableHead}
                rowCount={tableRows.length}
                numSelected={selected.length}
                onRequestSort={handleRequestSort}
                onSelectAllClick={handleSelectAllClick}
              />
              <TableBody>
                {filteredRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    const selectedTableRow = selected.indexOf(row.name) !== -1;
                    if (listType === "dineTables") {
                      return dineTableBody(row, selectedTableRow);
                    } else if (listType === "customerTables") {
                      return customerTableBody(row, selectedTableRow);
                    } else if (listType === "userTables") {
                      return userTableBody(row, selectedTableRow);
                    } else if (listType === "salesSummaryTables") {
                      return salesSummaryTableBody(row, selectedTableRow);
                    } else if (listType === "paymentMethodTables") {
                      return paymentMethodTableBody(row, selectedTableRow);
                    } else if (listType === "categorySalesTables") {
                      return categorySalesTableBody(row, selectedTableRow);
                    } else if (listType === "appSubscriptions") {
                      return appSubscriptionsTableBody(row, selectedTableRow);
                    } else if (listType === "categoryTables") {
                      return categoriesTableBody(row, selectedTableRow);
                    } else if (listType === "itemTables") {
                      return itemsTableBody(row, selectedTableRow);
                    } else if (listType === "addonsTables") {
                      return addonsTableBody(row, selectedTableRow);
                    } else if (listType === "attributeTables") {
                      return attributeTableBody(row, selectedTableRow);
                    }
                  })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={tableHead.length} />
                  </TableRow>
                )}
              </TableBody>

              {isNotFound && (
                <TableBody>
                  <TableRow>
                    <TableCell
                      align="center"
                      colSpan={tableHead.length}
                      sx={{ py: 3 }}
                    >
                      <Paper
                        sx={{
                          textAlign: "center",
                        }}
                      >
                        <Typography variant="h6" paragraph>
                          Not found
                        </Typography>

                        <Typography variant="body2">
                          No results found for &nbsp;
                          <strong>&quot;{filterName}&quot;</strong>.
                          <br /> Try checking for typos or using complete words.
                        </Typography>
                      </Paper>
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}

              {filteredRows.length === 0 && !filterName && !isLoading && (
                <TableBody>
                  <TableRow>
                    <TableCell align="center" colSpan={tableHead.length}>
                      <TableEmptyState
                        title={emptyTitle}
                        description={emptyDesc}
                        icon="mdi:folder-outline"
                        buttonLabel={emptyButtonLabel}
                        onButtonClick={handleAddAction}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              )}
            </Table>
          </TableContainer>
        </Scrollbar>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Card>
    </>
  );
}

export default AppTable;
